import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { Button } from './ui/button';
import { Shield, Smartphone, Users, Palette, Lock, BookOpen, Check, Zap } from 'lucide-react';

const VantagensPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Shield,
      title: "Proteção Anti-Pirataria",
      description: "Sistema avançado que bloqueia capturas de tela, cópia de texto e impressão não autorizada. Seus conteúdos protegidos pela Lei de Direitos Autorais nº 9.610/98.",
      color: "#ef4444"
    },
    {
      icon: Smartphone,
      title: "Leitura Multiplataforma",
      description: "Experiência de leitura otimizada para qualquer dispositivo. Seus assinantes podem ler no computador, tablet ou smartphone com a mesma qualidade.",
      color: "#3b82f6"
    },
    {
      icon: Users,
      title: "Gestão de Assinantes",
      description: "Painel completo para gerenciar seus assinantes. Acompanhe métricas de leitura, controle acessos e gerencie assinaturas de forma simples.",
      color: "#22c55e"
    },
    {
      icon: Palette,
      title: "Catálogo Personalizado",
      description: "Sua identidade visual em destaque. Personalize seu catálogo com cores, logo e banner exclusivos. Sua marca, sua experiência.",
      color: "#8b5cf6"
    },
    {
      icon: Lock,
      title: "Segurança Total",
      description: "Detecção automática de tentativas de captura. Quando detectado, o conteúdo é imediatamente protegido e o usuário é notificado.",
      color: "#f59e0b"
    },
    {
      icon: BookOpen,
      title: "Leitor Profissional",
      description: "Visualizador de PDFs completo com zoom, busca no documento, grifos coloridos, anotações pessoais e navegação intuitiva por páginas.",
      color: "#14b8a6"
    }
  ];

  return (
    <div className="vantagens-page" style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <Header />
      
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #0f2a4a 0%, #1a365d 100%)',
        padding: '6rem 2rem 4rem',
        textAlign: 'center',
        color: 'white'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h1 style={{ 
            fontSize: 'clamp(2rem, 5vw, 3rem)', 
            fontWeight: '700',
            marginBottom: '1.5rem',
            lineHeight: '1.2'
          }}>
            A Plataforma Completa para <span style={{ color: '#14b8a6' }}>Editoras Digitais</span>
          </h1>
          <p style={{ 
            fontSize: '1.2rem', 
            color: 'rgba(255,255,255,0.8)',
            lineHeight: '1.7',
            marginBottom: '2rem'
          }}>
            Publique seus livros e periódicos com segurança. Ofereça uma experiência de leitura premium 
            aos seus assinantes e monetize seu conteúdo de forma simples.
          </p>
          <Button 
            onClick={() => navigate('/cadastro')}
            style={{
              background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
              padding: '1rem 2.5rem',
              fontSize: '1.1rem',
              fontWeight: '600',
              borderRadius: '12px'
            }}
          >
            Começar Agora
          </Button>
        </div>
      </section>

      {/* Features Grid */}
      <section style={{ 
        padding: '5rem 2rem',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <h2 style={{
          textAlign: 'center',
          fontSize: '2rem',
          fontWeight: '700',
          color: '#1a202c',
          marginBottom: '3rem'
        }}>
          Por que escolher o Livrum?
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '2rem'
        }}>
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div 
                key={index}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '2rem',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                }}
              >
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '12px',
                  background: `${feature.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1.25rem'
                }}>
                  <IconComponent style={{ width: '28px', height: '28px', color: feature.color }} />
                </div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#1a202c',
                  marginBottom: '0.75rem'
                }}>
                  {feature.title}
                </h3>
                <p style={{
                  color: '#64748b',
                  lineHeight: '1.7',
                  fontSize: '0.95rem',
                  margin: 0
                }}>
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it Works */}
      <section style={{
        background: 'white',
        padding: '5rem 2rem'
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: '2rem',
            fontWeight: '700',
            color: '#1a202c',
            marginBottom: '3rem'
          }}>
            Como Funciona
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '2rem',
            textAlign: 'center'
          }}>
            {[
              { step: '1', title: 'Cadastre-se', desc: 'Crie sua conta de editora em poucos minutos' },
              { step: '2', title: 'Publique', desc: 'Faça upload dos seus livros e periódicos em PDF' },
              { step: '3', title: 'Personalize', desc: 'Configure seu catálogo com sua identidade visual' },
              { step: '4', title: 'Monetize', desc: 'Defina o valor da assinatura e comece a vender' }
            ].map((item, index) => (
              <div key={index}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  margin: '0 auto 1rem'
                }}>
                  {item.step}
                </div>
                <h4 style={{ color: '#1a202c', marginBottom: '0.5rem', fontWeight: '600' }}>
                  {item.title}
                </h4>
                <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Summary */}
      <section style={{
        background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
        padding: '4rem 2rem',
        textAlign: 'center',
        color: 'white'
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <Zap style={{ width: '48px', height: '48px', marginBottom: '1rem' }} />
          <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '1rem' }}>
            Modelo Simples e Transparente
          </h2>
          <p style={{ fontSize: '1.1rem', opacity: 0.9, marginBottom: '1.5rem' }}>
            <strong>R$ 3.000/ano</strong> + 30% sobre cada assinatura vendida
          </p>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            {['Publicações ilimitadas', 'Assinantes ilimitados', 'Proteção inclusa', 'Suporte dedicado'].map((item, i) => (
              <span key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'rgba(255,255,255,0.2)',
                padding: '0.5rem 1rem',
                borderRadius: '50px',
                fontSize: '0.9rem'
              }}>
                <Check style={{ width: '16px', height: '16px' }} />
                {item}
              </span>
            ))}
          </div>
          <Button 
            onClick={() => navigate('/cadastro')}
            style={{
              background: 'white',
              color: '#14b8a6',
              padding: '1rem 2.5rem',
              fontSize: '1.1rem',
              fontWeight: '600',
              borderRadius: '12px'
            }}
          >
            Criar Minha Conta
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VantagensPage;
